import type { VersionLabel, VersionView } from '@tabel/shared/schemas/rules';
import { useQuery } from '@tanstack/react-query';
import { Ban, Bot, History, Undo2 } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Badge, Button, Card, EmptyState, Skeleton } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { reportClientError } from '../../lib/errors';
import { useRole } from '../../lib/role';
import { LoadError, shouldReport } from './LoadError';
import { NotIntroduceDialog } from './NotIntroduceDialog';
import { RollbackDialog } from './RollbackDialog';
import { localDate } from './dates';
import { PLAN_LABEL } from './plan-texts';
import { LABEL, T, VERSION, VERSION_STATUS, say } from './texts';

/*
 * «История изменений» (004, FR-010, T047; contracts/ui.md, «Вид «История изменений»»): версии сверху новые — дата
 * публикации, статус (Badge), «Действует с», кто опубликовал, «Почему меняем», «Что решили», документ, связанный
 * вопрос, изменения словами (до 6, затем «и ещё N»), итог пересчёта (US3, T083: пока пересчёт идёт, история
 * спрашивает сервер снова). У версии возврата — к какой версии вернули и «Почему возвращаем».
 * Администратору правил и утверждающему без черновика — «Вернуть эти правила» у версий «действовала» и «Не вводить
 * эти правила» у версий «начнёт действовать» (US4, T089; FR-046, FR-047). При черновике кнопок нет, внизу — почему.
 * Если вернуть версию нельзя (`rollback.available: false`, сценарий US4 6), кнопка недоступна сразу, причина — рядом.
 * Версия, опубликованная по плану ИИ-агента (005, T123; FR-039; `preparedByAgent`), — метка «Подготовлено ИИ-агентом»
 * рядом со статусом.
 */

const TONE: Readonly<Record<VersionLabel, 'ok' | 'accent' | 'neutral'>> = {
  active: 'ok',
  future: 'accent',
  past: 'neutral',
  replaced: 'neutral',
  not_introduced: 'neutral',
};

/** Изменений словами в версии показано не больше. */
const MAX_CHANGES = 6;

/** Дата публикации по местному времени объекта: `YYYY-MM-DD` (`dates.ts`). */
export { localDate };

/** Пересчёт версии ещё идёт: история спрашивает сервер снова. */
const running = (v: VersionView) => v.recalc?.status === 'queued' || v.recalc?.status === 'running';
/** Как часто спрашивать итог пересчёта, пока он идёт. */
export const RECALC_POLL_MS = 2000;

/** Действия с версией: возврат — у «действовала», «Не вводить» — у «начнёт действовать». */
interface VersionActions {
  readonly onRollback: (v: VersionView) => void;
  readonly onNotIntroduce: (v: VersionView) => void;
}

/**
 * Итог пересчёта (FR-040, FR-041): «Система пересчитывает табели…», «Пересчитано N дней», «N дней не посчитаны»,
 * «В N днях часы после пересчёта не совпали с влиянием…». Числа — с сервера.
 */
function Recalc({ v }: { v: VersionView }) {
  const r = v.recalc;
  if (!r) return null;
  if (running(v)) return <p className="text-ink-2">{T.recalcRunning}</p>;
  return (
    <>
      {r.status === 'done' && <p className="text-ink-2">{say.recalcDone(r.days ?? 0, r.changed ?? 0)}</p>}
      {(r.failedDays ?? 0) > 0 && <p className="text-bad">{say.recalcFailed(r.failedDays ?? 0)}</p>}
      {(r.mismatch ?? 0) > 0 && <p className="text-warn">{say.recalcMismatch(r.mismatch ?? 0)}</p>}
    </>
  );
}

function Version({
  v,
  tzOffset,
  focus,
  actions,
}: {
  v: VersionView;
  tzOffset: string;
  focus: boolean;
  actions: VersionActions | null;
}) {
  const codes = v.basis.questionCodes ?? [];
  const rollback = v.basis.kind === 'возврат';
  const shown = v.changes.slice(0, MAX_CHANGES);
  const rest = v.changes.length - shown.length;
  const ref = useRef<HTMLLIElement>(null);
  // Ссылка «Новые версии…» ведёт сюда: версия видна и выделена.
  useEffect(() => {
    if (focus) ref.current?.scrollIntoView?.({ block: 'center' });
  }, [focus]);
  return (
    <li
      ref={ref}
      id={`version-${v.id}`}
      data-focus={focus || undefined}
      className={`grid scroll-mt-4 grid-cols-[132px_minmax(0,1fr)] gap-x-4 gap-y-2 px-5 py-4 max-sm:grid-cols-1 max-sm:px-4 ${focus ? 'bg-accent-soft' : ''}`}
    >
      <div
        data-version-meta
        className="flex flex-col items-start gap-1.5 text-[13px] text-ink-2 tabular-nums max-sm:flex-row max-sm:flex-wrap max-sm:items-center max-sm:gap-2"
      >
        <span>{say.published(localDate(v.publishedAt, tzOffset))}</span>
        <Badge tone={TONE[v.label]} dot>
          {VERSION_STATUS[v.label](v.validFrom)}
        </Badge>
        {v.preparedByAgent === true && (
          <Badge tone="accent">
            <Bot size={12} strokeWidth={1.75} aria-hidden />
            {PLAN_LABEL.prepared}
          </Badge>
        )}
      </div>
      <div className="min-w-0 space-y-1 text-[13px] break-words text-ink">
        <p className="text-[14px] font-semibold">
          {say.versionNumber(v.version)} · {say.validFrom(v.validFrom)}
        </p>
        {v.author && <p className="text-ink-2">{say.author(v.author.name)}</p>}
        <p>{say.basisKind(v.basis.kind)}</p>
        {rollback && v.basis.rollbackOf !== undefined && <p>{say.rollbackOf(v.basis.rollbackOf)}</p>}
        {v.basis.decided.trim() && (
          <p>{rollback ? say.rollbackWhy(v.basis.decided) : say.decided(v.basis.decided)}</p>
        )}
        {v.basis.doc && <p>{say.doc(v.basis.doc)}</p>}
        {codes.length > 0 && <p>{say.question(codes)}</p>}
        {v.notIntroduced && <p className="text-ink-2">{say.notIntroduced(v.notIntroduced.reason)}</p>}
        {v.changes.length > 0 && (
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-ink-2">
            {shown.map((c) => (
              <li key={`${c.group}.${c.field}`}>{c.text}</li>
            ))}
            {rest > 0 && <li className="list-none">{say.more(rest)}</li>}
          </ul>
        )}
        <Recalc v={v} />
        {actions && v.label === 'past' && <RollbackButton v={v} onRollback={actions.onRollback} />}
        {actions && v.label === 'future' && (
          <div className="pt-1.5">
            <Button icon={Ban} onClick={() => actions.onNotIntroduce(v)}>
              {VERSION.notIntroduce}
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

/** Почему вернуть версию нельзя (`VersionView.rollback.reason`): текст рядом с недоступной кнопкой. */
const ROLLBACK_BLOCKED: Readonly<
  Record<NonNullable<NonNullable<VersionView['rollback']>['reason']>, string>
> = {
  impl_gone: VERSION.rollbackImplGone,
};

/**
 * «Вернуть эти правила» у версии «действовала». Сервер сказал, что вернуть нельзя (`rollback.available: false`), —
 * кнопка недоступна сразу, до окна, а причина написана рядом (US4, сценарий 6). Нет поля `rollback` — кнопка доступна,
 * причину скажет окно возврата.
 */
function RollbackButton({ v, onRollback }: { v: VersionView; onRollback: (v: VersionView) => void }) {
  const id = useId();
  const blocked = v.rollback?.available === false;
  const why = blocked
    ? (ROLLBACK_BLOCKED[v.rollback?.reason ?? 'impl_gone'] ?? VERSION.rollbackImplGone)
    : null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1.5">
      <Button
        icon={Undo2}
        disabled={blocked}
        aria-describedby={why ? id : undefined}
        onClick={() => onRollback(v)}
      >
        {VERSION.rollback}
      </Button>
      {why && (
        <p id={id} className="min-w-0 basis-[260px] flex-1 text-[12.5px] text-ink-2">
          {why}
        </p>
      )}
    </div>
  );
}

/** Сверху новые: по дате публикации, при равенстве — по номеру версии. */
function newestFirst(a: VersionView, b: VersionView): number {
  return b.publishedAt.localeCompare(a.publishedAt) || b.version - a.version;
}

export function HistoryView({
  tzOffset,
  focus,
  draftOpen,
}: {
  tzOffset: string;
  focus?: number;
  /** Есть ли на объекте черновик; `undefined` — ещё неизвестно: кнопок возврата нет. */
  draftOpen?: boolean;
}) {
  const { role } = useRole();
  const [rollbackOf, setRollbackOf] = useState<VersionView | null>(null);
  const [notIntroduceOf, setNotIntroduceOf] = useState<VersionView | null>(null);
  // «Вернуть» и «Не вводить» — администратор правил и утверждающий (таблица «Права ролей»), только без черновика.
  const mayAct = role === 'rules_admin' || role === 'approver';
  const actions: VersionActions | null =
    mayAct && draftOpen === false ? { onRollback: setRollbackOf, onNotIntroduce: setNotIntroduceOf } : null;
  const q = useQuery({
    queryKey: rulesKeys.history(role),
    queryFn: async () => {
      try {
        return await api.rulesHistory();
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'load history', e);
        throw e;
      }
    },
    // Пока пересчёт идёт, итог приходит позже (FR-041): история спрашивает снова.
    refetchInterval: (query) => ((query.state.data ?? []).some(running) ? RECALC_POLL_MS : false),
  });
  if (q.isLoading)
    return (
      <div aria-busy="true" aria-label={LABEL.loading} className="space-y-3">
        <Skeleton className="h-24 w-full rounded-card" />
        <Skeleton className="h-24 w-full rounded-card" />
      </div>
    );
  if (q.error)
    return <LoadError error={q.error} title={LABEL.historyError} onRetry={() => void q.refetch()} />;
  const versions = [...(q.data ?? [])].sort(newestFirst);
  if (versions.length === 0)
    return (
      <Card>
        <EmptyState icon={History} title={T.historyEmpty} />
      </Card>
    );
  return (
    <div className="space-y-3">
      <Card>
        <ol aria-label={LABEL.versions} className="divide-y divide-line">
          {versions.map((v) => (
            <Version key={v.id} v={v} tzOffset={tzOffset} focus={v.id === focus} actions={actions} />
          ))}
        </ol>
      </Card>
      {mayAct && draftOpen === true && <p className="text-[13px] text-ink-2">{VERSION.draftOpen}</p>}
      <RollbackDialog version={rollbackOf} onClose={() => setRollbackOf(null)} />
      <NotIntroduceDialog version={notIntroduceOf} onClose={() => setNotIntroduceOf(null)} />
    </div>
  );
}
